import { Article, channel, SuccessArticleErpInfo } from "@tm/models"
import { useEffect, useMemo } from "react"
import { mapWholesalerArticleToArticle } from "@bundles/parts/components/ListV2/helpers"
import { ErpInfosContext, ErpInfosProviderProps } from "@bundles/parts/components/ListV2/ContextProvider/ErpInfos"
import { useArticlesContext, useTradeReferenceContext } from "@bundles/parts/components/ListV2/ContextProvider"
import { useIsNoTeccomRequestAvailable } from "@bundles/parts/components/ListV2/ContextProvider/ErpInfos/useIsNoTeccomRequestAvailable"
import { useHasNoErpAreaIcons } from "@bundles/parts/components/ListV2/ContextProvider/ErpInfos/useHasNoErpAreaIcons"
import { useErpInfos } from "./useErpInfos"

export function MultiErpInfosProvider({ erpInfos, setErpInfos, children }: ErpInfosProviderProps) {
    const { articles, wholesalerArticles } = useArticlesContext()
    const { tradeReferences, tradeReferenceNumbersLoaded } = useTradeReferenceContext()

    const erpArticles = useMemo(() => {
        const wholesalerToArticle = wholesalerArticles?.map((x) => mapWholesalerArticleToArticle(x)) ?? []
        const linkedArticles = articles.map((article) => article.linkedArticle).filter((x) => !!x) ?? []

        if (linkedArticles.length > 0) {
            return [...articles, ...wholesalerToArticle, ...linkedArticles] as Article[]
        }

        return [...articles, ...wholesalerToArticle] as Article[]
    }, [articles, wholesalerArticles])

    const baseErpInfosData = useErpInfos({
        articles: erpArticles,
        isEnabled: tradeReferenceNumbersLoaded,
        erpInfos,
        setErpInfos,
        tradeReferences,
    })

    const isNoTeccomRequestAvailable = useIsNoTeccomRequestAvailable(erpInfos, articles, wholesalerArticles)
    const hasNoErpAreaIcons = useHasNoErpAreaIcons(erpInfos, articles, wholesalerArticles)

    const erpInfosData = useMemo(() => {
        return { ...baseErpInfosData, isNoTeccomRequestAvailable, hasNoErpAreaIcons }
    }, [baseErpInfosData, isNoTeccomRequestAvailable, hasNoErpAreaIcons])

    useEffect(() => {
        if (erpInfosData.isReady && erpInfosData.erpInfos.every((x) => x.state === "success")) {
            channel("GLOBAL").publish(
                "ERP/ERP_INFORMATION_LOADED",
                erpInfosData.erpInfos.map((x) => (x as SuccessArticleErpInfo).response)
            )
        }
    }, [erpInfosData.erpInfos])

    return <ErpInfosContext.Provider value={erpInfosData}>{children}</ErpInfosContext.Provider>
}
